/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { ArrowLeftOutlined, ArrowRightOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { Drawer, Form } from 'antd';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ConfigRevision, Node, NodeConfig } from '@/client';
import { useMessage } from '@/hooks/message';
import { useCreateNodeGroupMutation } from '@/hooks/mutation/useCreateNodeGroupMutation';
import { usePaginatedConfigRevisionsQuery } from '@/hooks/query/usePaginatedConfigRevisionsQuery';
import { usePaginatedNodeConfigsQuery } from '@/hooks/query/usePaginatedNodeConfigsQuery';
import { usePaginatedNodesQuery } from '@/hooks/query/usePaginatedNodesQuery';
import { usePaginatedTagsQuery } from '@/hooks/query/usePaginatedTagsQuery';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Button } from '@/primitives/Button';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

import './NewFleetWizard.css';
import { FleetDetailsStep } from './NewFleetWizardSteps/FleetDetailsStep';
import { ReviewStep } from './NewFleetWizardSteps/ReviewStep';
import { SelectConfigStep } from './NewFleetWizardSteps/SelectConfigStep';
import { SelectNodesStep } from './NewFleetWizardSteps/SelectNodesStep';
import { SelectRevisionStep } from './NewFleetWizardSteps/SelectRevisionStep';
import { NewFleetFormFields } from './NewFleetWizardSteps/types';

export type { NewFleetFormFields } from './NewFleetWizardSteps/types';

type WizardStep = 'details' | 'selectConfig' | 'selectRevision' | 'selectNodes' | 'review';

export type NewFleetWizardProps = {
  open: boolean;
  onClose: () => void;
};

export function NewFleetWizard({ open, onClose }: NewFleetWizardProps) {
  const [form] = Form.useForm<NewFleetFormFields>();
  const [message] = useMessage();
  const { t } = useMimicTranslation('fleets');
  const [currentStep, setCurrentStep] = useState<WizardStep>('details');
  const [selectedConfig, setSelectedConfig] = useState<NodeConfig | undefined>();
  const [selectedRevision, setSelectedRevision] = useState<ConfigRevision | undefined>();
  const [formValues, setFormValues] = useState<NewFleetFormFields | undefined>();
  const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>([]);
  const [selectedNodes, setSelectedNodes] = useState<Node[]>([]);
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const navigate = useNavigate();

  const operatingSystem = Form.useWatch('operatingSystem', form);

  const nodesQuery = usePaginatedNodesQuery({
    params: {
      filters: { operatingSystem: formValues?.operatingSystem, hasNodeGroup: false },
    },
  });
  const updateNodesQueryParams = nodesQuery.updateQueryParams;

  const tagsQuery = usePaginatedTagsQuery({ params: { page: 1, pageSize: 100 } });

  const configsQuery = usePaginatedNodeConfigsQuery({
    params: {
      filters: { os: operatingSystem },
    },
    type: 'inMemory',
  });
  const updateConfigsQueryParams = configsQuery.updateQueryParams;

  const revisionsQuery = usePaginatedConfigRevisionsQuery({
    params: {
      configId: selectedConfig?.id || '',
    },
    type: 'inMemory',
  });

  // Update filter and clear selected config when OS changes
  useEffect(() => {
    setSelectedConfig(undefined);
    setSelectedRevision(undefined);
    if (operatingSystem) {
      updateConfigsQueryParams({ filters: { os: operatingSystem } });
    }
  }, [operatingSystem, updateConfigsQueryParams]);

  // Clear selected revision when config changes
  useEffect(() => {
    setSelectedRevision(undefined);
  }, [selectedConfig?.id]);

  // Update filter and clear selected nodes when OS changes
  useEffect(() => {
    setSelectedNodeIds([]);
    setSelectedNodes([]);
    if (operatingSystem) {
      updateNodesQueryParams({ filters: { operatingSystem } });
    }
  }, [operatingSystem, updateNodesQueryParams]);

  const { mutate: createNodeGroup, isPending } = useCreateNodeGroupMutation({
    onSuccess: (data) => {
      message.success(t('wizard.feedback.fleetCreatedSuccess'));
      handleClose();
      navigate(`/tenants/${tenantId}/fleet-management/${data.data.id}`);
    },
    onError: () => {
      message.error(t('wizard.feedback.fleetCreatedError'));
    },
  });

  const handleClose = () => {
    form.resetFields();
    setCurrentStep('details');
    setSelectedConfig(undefined);
    setSelectedRevision(undefined);
    setFormValues(undefined);
    setSelectedNodeIds([]);
    setSelectedNodes([]);
    onClose();
  };

  const handleNext = async () => {
    if (currentStep === 'details') {
      try {
        await form.validateFields();
        const values = form.getFieldsValue();
        setFormValues(values);
        setCurrentStep('selectConfig');
      } catch {
        // Form validation failed
      }
    } else if (currentStep === 'selectConfig') {
      setCurrentStep('selectRevision');
    } else if (currentStep === 'selectRevision') {
      setCurrentStep('selectNodes');
    } else if (currentStep === 'selectNodes') {
      setCurrentStep('review');
    }
  };

  const handlePrevious = () => {
    if (currentStep === 'selectConfig') {
      setCurrentStep('details');
    } else if (currentStep === 'selectRevision') {
      setCurrentStep('selectConfig');
    } else if (currentStep === 'selectNodes') {
      setCurrentStep('selectRevision');
    } else if (currentStep === 'review') {
      setCurrentStep('selectNodes');
    }
  };

  const handleSubmit = async () => {
    if (!selectedConfig || !selectedRevision || !formValues) return;

    createNodeGroup({
      name: formValues.name,
      description: formValues.description,
      operatingSystem: formValues.operatingSystem,
      configId: selectedConfig.id,
      configRevisionId: selectedRevision.id,
      nodeIds: selectedNodeIds.length > 0 ? selectedNodeIds : undefined,
    });
  };

  const handleNodeSelectionChange = (nodeIds: string[], nodes: Node[]) => {
    setSelectedNodeIds(nodeIds);
    setSelectedNodes(nodes);
  };

  const getNodeConfigPath = (nodeConfig: NodeConfig) => `/tenants/${tenantId}/node-configs/${nodeConfig.id}`;
  const getNodeConfigRevisionPath = (revision: ConfigRevision) =>
    `/tenants/${tenantId}/node-configs/${revision.nodeConfigId}/revisions/${revision.revisionNumber}`;
  const getNodeConfigurationPath = (nodeConfigId: string) => `/tenants/${tenantId}/node-configs/${nodeConfigId}`;

  const canProceed: Record<WizardStep, boolean> = {
    details: true,
    selectConfig: !!selectedConfig,
    selectRevision: !!selectedRevision,
    selectNodes: true,
    review: true,
  };

  const stepTitles: Record<WizardStep, string> = {
    details: t('wizard.steps.details'),
    selectConfig: t('wizard.steps.selectConfig'),
    selectRevision: t('wizard.steps.selectRevision'),
    selectNodes: t('wizard.steps.selectNodes'),
    review: t('wizard.steps.review'),
  };

  const footer = (
    <div className="new-fleet-wizard-footer">
      <div className="left-buttons">
        {currentStep !== 'details' && (
          <Button dataTestId="previous-step" type="default" onClick={handlePrevious} disabled={isPending}>
            <ArrowLeftOutlined />
            {t('wizard.previous')}
          </Button>
        )}
      </div>
      <div className="right-buttons">
        <Button dataTestId="cancel-new-fleet" type="link" onClick={handleClose} disabled={isPending}>
          {t('cancel')}
        </Button>
        {currentStep !== 'review' && (
          <Button dataTestId="next-step" type="primary" onClick={handleNext} disabled={!canProceed[currentStep]}>
            {t('wizard.next')}
            <ArrowRightOutlined />
          </Button>
        )}
        {currentStep === 'review' && (
          <Button
            dataTestId="create-fleet"
            type="primary"
            onClick={handleSubmit}
            disabled={isPending}
            loading={isPending}
          >
            {t('wizard.createFleet')}
            <CheckOutlined />
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <Drawer
      rootClassName="new-fleet-wizard"
      placement="top"
      title={stepTitles[currentStep]}
      open={open}
      height="100vh"
      onClose={handleClose}
      closeIcon={null}
      extra={<CloseButton onClick={handleClose} />}
      footer={footer}
    >
      {currentStep === 'details' && <FleetDetailsStep form={form} isPending={isPending} />}

      {currentStep === 'selectConfig' && (
        <SelectConfigStep
          configsQuery={configsQuery}
          selectedConfig={selectedConfig}
          onConfigSelect={setSelectedConfig}
          getNodeConfigPath={getNodeConfigPath}
          getNodeConfigRevisionPath={getNodeConfigRevisionPath}
        />
      )}

      {currentStep === 'selectRevision' && selectedConfig && (
        <SelectRevisionStep
          revisionsQuery={revisionsQuery}
          selectedRevision={selectedRevision}
          onRevisionSelect={setSelectedRevision}
          getConfigRevisionPath={getNodeConfigRevisionPath}
        />
      )}

      {currentStep === 'selectNodes' && (
        <SelectNodesStep
          tenantId={tenantId!}
          nodesQuery={nodesQuery}
          tagsQuery={tagsQuery}
          selectedNodeIds={selectedNodeIds}
          onSelectionChange={handleNodeSelectionChange}
          getNodeConfigurationPath={getNodeConfigurationPath}
        />
      )}

      {currentStep === 'review' && formValues && selectedConfig && selectedRevision && (
        <ReviewStep
          tenantId={tenantId!}
          formValues={formValues}
          selectedConfig={selectedConfig}
          selectedRevision={selectedRevision}
          selectedNodes={selectedNodes}
          getNodeConfigurationPath={getNodeConfigurationPath}
        />
      )}
    </Drawer>
  );
}

function CloseButton({ onClick }: { onClick: () => void }) {
  return (
    <Button dataTestId="close-new-fleet-wizard" type="text" onClick={onClick}>
      <CloseOutlined />
    </Button>
  );
}
